import { html, Page } from 'rune-ts';
import klass from './Partners.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '@services/MarppleShop/renderApp/components/cells/Footer/constant.ts';
import { BaseLayoutOptions } from '@services/MarppleShop/features/Core/service';
import { FloatingButton_recent_product } from '@services/MarppleShop/renderApp/components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product.ts';
import { FloatingBox } from '@services/MarppleShop/renderApp/components/cells/FloatingBox/FloatingBox.ts';
import type { ProductCardViewData } from '@services/MarppleShop/features/ProductList/type';
import type { CreatorSearchData } from '@services/MarppleShop/features/Creator/type';
import { TabListView } from '@services/MarppleShop/shared/components/cells/TabList/TabListView';
import { CoreUiS } from '@services/MarppleShop/features/Core/outbound/share';
import { CreatorSearchCard } from '../../components/cells/CreatorSearchCard/CreatorSearchCard';
import { ButtonView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/Buttons/ButtonView.ts';
import { makeUtilScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { BecomeFanButtonController } from '@services/MarppleShop/renderApp/components/cells/CreatorMainProfile/controllers/BecomeFanController.ts';
import { goLogin } from '@services/MarppleShop/shared/util/goLogin.ts';

export type PartnersPageData = {
  layout_options: BaseLayoutOptions;
  recent_product?: ProductCardViewData;
  creators: CreatorSearchData[];
};

class PartnersBodyView extends Page<PartnersPageData> {
  private buttonView = new ButtonView({
    size: this.data.layout_options.is_mobile ? 40 : 48,
    color: 'Primary_A',
    as: 'a',
    href: `/${ET.lang}/@/goodspartner`,
    dataset: {
      postMessage: dataStr(makeUtilScreenNavigate(`/${ET.lang}/@/goodspartner`)),
    },
    target: '_blank',
    label: ET('mps2::partners::apply_button'),
  });

  protected override template() {
    const { is_mobile } = this.data.layout_options;

    return html`<div class="${klass.partners}">
      <div class="${klass.body}">
        ${is_mobile
          ? html`<div class="${klass.tab_list_wrapper}">
              ${new TabListView({
                tabs: [
                  {
                    tab_title: ET('mps2::section_title::section_tabs::partners'),
                    href: `/${ET.lang}/@/creator/partners`,
                    selected: true,
                  },
                  {
                    tab_title: ET('mps2::section_title::section_tabs::creator_search'),
                    href: `/${ET.lang}/@/creator/explore`,
                    selected: false,
                  },
                  {
                    tab_title: ET('mps2::section_title::section_tabs::plus_shop'),
                    href: `/${ET.lang}/@/creator/plus`,
                    selected: false,
                  },
                ],
              })}
            </div>`
          : html`<div class="${klass.title}">${CoreUiS.makeCreatorRankingTitle(false, 'partners')}</div>`}
        <div class="${klass.partners_apply}">
          <div class="${klass.apply_titles}">
            ${is_mobile
              ? html`
                  <div class="${klass.apply_sub_title} ${font('14_medium')}">
                    ${ET('mps2::partners::apply_sub_title_mo')}
                  </div>
                  <div class="${klass.apply_title} ${font('24_bold')}">
                    ${html.preventEscape(ET('mps2::partners::apply_title_mo'))}
                  </div>
                  <div class="${klass.apply_button}">${this.buttonView}</div>
                `
              : html` <div class="${klass.apply_sub_title} ${font('16_semibold')}">
                    ${ET('mps2::partners::apply_sub_title')}
                  </div>
                  <div class="${klass.apply_title} ${font('32_bold')}">
                    ${html.preventEscape(ET('mps2::partners::apply_title'))}
                  </div>
                  <div class="${klass.apply_description} ${font('16_medium')}">
                    ${html.preventEscape(ET('mps2::partners::apply_description'))}
                  </div>
                  <div class="${klass.apply_button}">${this.buttonView}</div>`}
          </div>
        </div>
        <div class="${klass.creators_container}">
          ${this.data.creators.map((creator, index) => {
            return new CreatorSearchCard({
              creator,
              is_mobile,
              is_lazy: is_mobile && index > 5,
            });
          })}
        </div>
      </div>
      <div class="${klass.footer}">
        <div class="${klass.footer_bg1}"></div>
        <div class="${klass.footer_bg2}"></div>
        <div class="${klass.footer_apply_titles}">
          <div class="${klass.footer_apply_title} ${font('28_bold', '20_bold')}">
            ${html.preventEscape(ET('mps2::partners::footer_apply_title'))}
          </div>
          <div class="${klass.footer_apply_description} ${font('16_medium', '14_medium')}">
            ${html.preventEscape(ET('mps2::partners::footer_apply_description'))}
          </div>
          <div class="${klass.footer_apply_button}">${this.buttonView}</div>
        </div>
      </div>
    </div> `;
  }

  protected override onRender() {
    this.delegate(CreatorSearchCard.FanButtonClickEvent, CreatorSearchCard, async (e, Card) => {
      if (this.data.layout_options.user?.id) {
        const { id, is_liked } = e.detail;

        Card.setLikeState(!!is_liked);

        BecomeFanButtonController.postLike({ is_liked: !!is_liked, store_id: id }, () => {
          Card.setLikeState(!is_liked);
        });
      } else {
        await goLogin();
      }
    });
  }
}

export class PartnersPage extends Page<PartnersPageData> {
  protected override template() {
    const { layout_options } = this.data;

    return html`
      <div class="${klass.page}">
        ${new LayoutView(
          {
            body: new PartnersBodyView(this.data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            ...layout_options,
            layout_klass: '',
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: 3,
            full_width: true,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(this.data.recent_product),
              async renderHook(view: FloatingBox) {
                await FloatingBox.refreshRecentProductFloatingButton(view);
              },
            },
          },
        )}
      </div>
    `;
  }
}
